"use client";

import type { ReactNode } from 'react';

import useInteractionTracker from '@blueshift-ui/telemetry/dist/hooks/use-interaction-tracker';
import {
  CheckCircle,
  Lightbulb,
  RotateCcw,
  SearchCheck,
  X,
} from 'lucide-react';
import { useState } from 'react';

import type { DifficultyLevel } from '../constants';
import type { VideoProps } from '../types/video';

import DifficultyBadge from './difficulty-badge';
import VideoSection from './video-section';

interface Question {
  correct: number;
  explanation?: ReactNode;
  options: ReactNode[];
  question: ReactNode;
}

interface SingleChoiceExerciseProps extends VideoProps {
  buttonText?: {
    checkAnswers?: string;
    reset?: string;
    tryAgain?: string;
  };
  difficulty: DifficultyLevel;
  problemNumber: number;
  questions: Question[];
  title: string;
}

const SingleChoiceExercise = ({
  buttonText = { checkAnswers: 'Check Answer', reset: 'Reset', tryAgain: 'Try Again' },
  difficulty,
  problemNumber,
  questions,
  title,
  videoData,
  videoId,
}: SingleChoiceExerciseProps) => {
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const { trackEvent } = useInteractionTracker();

  const handleAnswerSelect = (questionIndex: number, optionIndex: number) => {
    if (showResults) return; // Prevent changing answers after results are shown
    setSelectedAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[questionIndex] = optionIndex;
      return newAnswers;
    });
    void trackEvent({
      action: 'select-answer',
      category: 'exercise',
      label: `question-${questionIndex + 1}-choice-${String.fromCharCode(65 + optionIndex)}`,
    });
  };

  const checkAnswers = () => {
    setShowResults(true);
    void trackEvent({ action: 'submit-exercise', category: 'exercise', label: `problem-${problemNumber}` });
  };

  const resetQuiz = () => {
    setSelectedAnswers([]);
    setShowResults(false);
    void trackEvent({ action: 'try-again-exercise', category: 'exercise', label: `problem-${problemNumber}` });
  };

  // Check if all answers are correct
  const allAnswersCorrect = showResults && questions.every((question, index) => selectedAnswers[index] === question.correct);

  // Get difficulty-based styling for problem number circle
  const getDifficultyCircleStyle = (difficulty: DifficultyLevel) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-500 border-2 border-green-400';
      case 'Intermediate':
        return 'bg-amber-500 border-2 border-amber-400';
      case 'Advanced':
        return 'bg-red-500 border-2 border-red-400';
      default:
        return 'bg-green-500 border-2 border-green-400';
    }
  };

  // Get difficulty-based stripe color
  const getDifficultyStripe = (difficulty: DifficultyLevel) => {
    switch (difficulty) {
      case 'Beginner':
        return 'from-green-500 to-green-400';
      case 'Intermediate':
        return 'from-amber-500 to-amber-400';
      case 'Advanced':
        return 'from-red-500 to-red-400';
      default:
        return 'from-blue-500 to-blue-400';
    }
  };

  return (
    <div className="surface-card rounded-xl shadow-lg mb-8 overflow-hidden hover:shadow-xl transition-shadow">
      <div
        className={`h-2 bg-gradient-to-r ${getDifficultyStripe(difficulty)}`}
      />
      <div className="border-b border-glass p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <div
              className={`flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl text-white font-bold text-base sm:text-lg flex-shrink-0 ${getDifficultyCircleStyle(difficulty)}`}
            >
              {problemNumber}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-xl font-bold text-primary mb-1 truncate">
                {title}
              </h3>
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <span className="text-secondary">Single Choice Quiz</span>
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <DifficultyBadge difficulty={difficulty} />
          </div>
        </div>
      </div>

      <div className="p-6">
        {videoData && videoId && videoData.src && (
          <VideoSection videoData={videoData} videoId={videoId} />
        )}

        <div className="space-y-6">
          {questions.map((question, questionIndex) => (
            <div
              className="surface-elevated border border-glass rounded-xl p-6 no-math"
              key={questionIndex}
            >
              <div className="flex items-start gap-4 mb-6">
                <h4 className="font-semibold text-primary text-lg flex-1">
                  {question.question}
                </h4>
              </div>

              <div className="space-y-4">
                {question.options.map((option, optionIndex) => {
                  const isSelected =
                    selectedAnswers[questionIndex] === optionIndex;
                  const isCorrect = optionIndex === question.correct;
                  const isWrong = showResults && isSelected && !isCorrect;

                  let buttonClasses =
                    'w-full text-left p-4 rounded-xl border transition-all duration-300 flex items-center justify-between group';

                  // Add hover effects only when not showing results (not disabled)
                  if (!showResults) {
                    buttonClasses +=
                      ' hover:translate-y-[-2px] hover:shadow-lg';
                  }

                  if (showResults) {
                    // Disabled state when showing results
                    buttonClasses += ' cursor-not-allowed opacity-75';
                    if (isCorrect) {
                      buttonClasses +=
                        ' bg-status-correct/10 border-status-correct/30 text-status-correct';
                    } else if (isWrong) {
                      buttonClasses +=
                        ' bg-status-incorrect/10 border-status-incorrect/30 text-status-incorrect';
                    } else {
                      buttonClasses +=
                        ' bg-surface-hover border-glass text-tertiary';
                    }
                  } else if (isSelected) {
                    buttonClasses +=
                      ' bg-status-selected/10 border-status-selected/30 text-status-selected';
                  } else {
                    buttonClasses +=
                      ' bg-surface-secondary border-option-default text-secondary hover:bg-surface-hover hover:border-option-hover hover:text-primary';
                  }

                  return (
                    <button
                      aria-label={`Select option ${String.fromCharCode(65 + optionIndex)}: ${option}`}
                      aria-pressed={isSelected}
                      className={buttonClasses}
                      disabled={showResults}
                      key={optionIndex}
                      onClick={() =>
                        !showResults &&
                        handleAnswerSelect(questionIndex, optionIndex)
                      }
                      type="button"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold ${
                            showResults && isCorrect
                              ? 'border-status-correct bg-status-correct/20 text-status-correct'
                              : showResults && isWrong
                                ? 'border-status-incorrect bg-status-incorrect/20 text-status-incorrect'
                                : isSelected
                                  ? 'border-status-selected bg-status-selected/20 text-status-selected'
                                  : 'border-tertiary bg-surface-interactive text-tertiary'
                          }`}
                        >
                          {String.fromCharCode(65 + optionIndex)}
                        </div>
                        <span>{option}</span>
                      </div>
                      {showResults && isCorrect && (
                        <CheckCircle
                          aria-hidden="true"
                          className="w-5 h-5 text-status-correct"
                        />
                      )}
                      {showResults && isWrong && (
                        <X className="w-5 h-5 text-status-incorrect" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          {!showResults ? (
            <button
              aria-describedby="quiz-instructions"
              aria-label={`${buttonText.checkAnswers} - Check your answers for ${questions.length} question${questions.length > 1 ? 's' : ''}`}
              className="cta-button-primary px-8"
              disabled={selectedAnswers.length !== questions.length}
              onClick={checkAnswers}
              type="button"
            >
              <SearchCheck aria-hidden="true" className="w-4 h-4" />
              {buttonText.checkAnswers}
            </button>
          ) : (
            <button
              aria-label={`${allAnswersCorrect ? buttonText.reset : buttonText.tryAgain} - ${allAnswersCorrect ? 'Reset the quiz' : 'Reset the quiz to try again'}`}
              className="cta-button-primary px-8"
              onClick={resetQuiz}
              type="button"
            >
              <RotateCcw aria-hidden="true" className="w-4 h-4" />
              {allAnswersCorrect ? buttonText.reset : buttonText.tryAgain}
            </button>
          )}
        </div>

        {/* Hidden instructions for screen readers */}
        <div className="sr-only" id="quiz-instructions">
          {!showResults &&
            selectedAnswers.length < questions.length &&
            `Please select an answer for all ${questions.length} questions before checking your answers. ${questions.length - selectedAnswers.length} question${questions.length - selectedAnswers.length > 1 ? 's' : ''} remaining.`}
        </div>

        {/* Screen reader announcement for quiz completion */}
        <div aria-atomic="true" aria-live="polite" className="sr-only">
          {showResults &&
            'Quiz completed. Results and explanations are now available.'}
        </div>

        {/* Show explanations below the button after results are shown */}
        {showResults && (
          <div className="mt-6 space-y-6">
            {questions.map((question, questionIndex) => {
              if (!question.explanation) return null;

              return (
                <div
                  className="solution-box animate-fade-in-scale-bounce no-math"
                  key={questionIndex}
                >
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-exercise-question mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-primary mb-4">
                        Explanation
                      </h4>
                      <div className="text-secondary leading-relaxed">
                        {question.explanation}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleChoiceExercise;
